import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Stack, Button, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Breadcrumbs, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Divider,
    useTheme, useMediaQuery
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const INVOICE_STATUS_COLORS: any = {
    PAID: { bg: '#E6FFFA', text: '#38A169' },
    PARTIALLY_PAID: { bg: '#FFFAF0', text: '#DD6B20' },
    UNPAID: { bg: '#FFF5F5', text: '#E53E3E' },
    OVERDUE: { bg: '#742A2A', text: '#FFFFFF' },
};

const InvoicesPage = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState<any[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [students, setStudents] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [feeStructures, setFeeStructures] = useState<any[]>([]);

    const [formData, setFormData] = useState({
        studentId: '',
        dueDate: new Date().toISOString().split('T')[0],
        term: 'Term 1',
        items: [{ description: '', amount: '', incomeAccountId: '' }],
        receivableAccountId: ''
    });

    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchInvoices = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('invoices'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvoices(res.data);
            if (res.data.length > 0 && !selectedInvoice) {
                setSelectedInvoice(res.data[0]);
            }
        } catch (error) {
            console.error('Fetch invoices error:', error);
        }
    }, [selectedInvoice]);

    const fetchData = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const [stdRes, accRes, feeRes] = await Promise.all([
                axios.get(getApiUrl('students'), config),
                axios.get(getApiUrl('accounts'), config),
                axios.get(getApiUrl('fees/structures'), config)
            ]);
            setStudents(stdRes.data);

            const allAccounts = accRes.data;
            const receivableAcc = allAccounts.find((a: any) => a.code === '1200');
            setAccounts(allAccounts);
            if (receivableAcc) {
                setFormData(prev => ({ ...prev, receivableAccountId: receivableAcc.id }));
            }

            setFeeStructures(feeRes.data);

            const studentId = searchParams.get('studentId');
            const action = searchParams.get('action');
            if (studentId && action === 'new') {
                setFormData(prev => ({ ...prev, studentId }));
                setOpenDialog(true);
            }

        } catch (error) {
            console.error('Fetch data error:', error);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchInvoices();
        fetchData();
    }, [fetchInvoices, fetchData]);

    const handleCreateInvoice = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl('invoices'), formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpenDialog(false);
            setFormData({
                studentId: '',
                dueDate: new Date().toISOString().split('T')[0],
                term: 'Term 1',
                items: [{ description: '', amount: '', incomeAccountId: '' }],
                receivableAccountId: formData.receivableAccountId
            });
            fetchInvoices();
        } catch (error: any) {
            console.error('Create invoice error:', error.response?.data?.message || error.message);
            alert(`Failed to create invoice: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleAddItem = () => {
        setFormData({ ...formData, items: [...formData.items, { description: '', amount: '', incomeAccountId: '' }] });
    };

    const handleDeleteItem = (index: number) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };

    const handleLoadFees = () => {
        if (!formData.studentId || !formData.term) {
            alert('Please select a Student and Term first.');
            return;
        }

        const student = students.find(s => s.id === formData.studentId);
        if (!student) return;

        const currentYear = new Date(formData.dueDate).getFullYear();

        const structure = feeStructures.find(fs =>
            fs.grade === student.grade &&
            fs.term === formData.term &&
            fs.academicYear === currentYear
        );

        if (structure) {
            if (window.confirm(`Found Fee Structure: "${structure.name}" ($${Number(structure.totalAmount).toLocaleString()}).\n\nLoad these items?`)) {
                setFormData(prev => ({
                    ...prev,
                    items: structure.items.map((item: any) => ({
                        description: item.description,
                        amount: item.amount.toString(),
                        incomeAccountId: ''
                    }))
                }));
            }
        } else {
            if (window.confirm(`No Fee Structure found for ${student.grade} - ${formData.term} ${currentYear}.\n\nWould you like to create one now?`)) {
                navigate(`/dashboard/fees/create?grade=${encodeURIComponent(student.grade)}&term=${encodeURIComponent(formData.term)}&academicYear=${currentYear}&studentId=${formData.studentId}&returnTo=/dashboard/invoices&action=new`);
            }
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        inv.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${inv.student.firstName} ${inv.student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} spacing={isMobile ? 1 : 0}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Invoices</Typography>
                    <Typography variant="body2" color="text.secondary">Manage student billing and receivables</Typography>
                </Box>
                {!isMobile && (
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Accounts</Typography>
                        <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Invoices</Typography>
                    </Breadcrumbs>
                )}
            </Stack>

            <Stack direction={isSmallMobile ? "column" : "row"} justifyContent="flex-end" spacing={2} mb={3}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ bgcolor: '#263B7C', borderRadius: '8px', px: 3, width: isSmallMobile ? '100%' : 'auto' }}
                >
                    NEW INVOICE
                </Button>
            </Stack>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                        <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                placeholder="Search Invoice or Student..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                slotProps={{ input: { startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> } }}
                            />
                        </Box>

                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>INVOICE NO</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>STUDENT</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>PAID</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>BALANCE</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>TERM</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>STATUS</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>DUE DATE</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="center">ACTION</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredInvoices.map((inv) => (
                                        <TableRow
                                            key={inv.id}
                                            hover
                                            onClick={() => setSelectedInvoice(inv)}
                                            selected={selectedInvoice?.id === inv.id}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, color: '#263B7C' }}>{inv.invoiceNo}</TableCell>
                                            <TableCell>{inv.student.firstName} {inv.student.lastName}</TableCell>
                                            <TableCell>${Number(inv.totalAmount).toLocaleString()}</TableCell>
                                            <TableCell sx={{ color: '#38A169', fontWeight: 600 }}>
                                                ${inv.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0).toLocaleString() || '0'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: (Number(inv.totalAmount) - (inv.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0)) > 0 ? '#C53030' : 'inherit' }}>
                                                ${(Number(inv.totalAmount) - (inv.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0)).toLocaleString()}
                                            </TableCell>
                                            <TableCell>{inv.term}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={inv.status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: INVOICE_STATUS_COLORS[inv.status]?.bg,
                                                        color: INVOICE_STATUS_COLORS[inv.status]?.text,
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(inv.dueDate).toLocaleDateString()}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#263B7C' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/dashboard/invoices/${inv.id}`);
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                    {selectedInvoice ? (
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>Invoice Details</Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">INVOICE NO</Typography>
                                    <Typography fontWeight={600} color="#263B7C">{selectedInvoice.invoiceNo}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">STUDENT</Typography>
                                    <Typography fontWeight={600}>{selectedInvoice.student.firstName} {selectedInvoice.student.lastName}</Typography>
                                </Box>
                                <Divider />
                                <Typography fontWeight={700} sx={{ mt: 2 }}>Items</Typography>
                                {selectedInvoice.items?.map((item: any) => (
                                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{item.description}</Typography>
                                        <Typography variant="body2" fontWeight={600}>${Number(item.amount).toLocaleString()}</Typography>
                                    </Box>
                                ))}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                                    <Typography fontWeight={700}>Total Amount</Typography>
                                    <Typography fontWeight={700} color="#263B7C">${Number(selectedInvoice.totalAmount).toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
                                    <Typography variant="body2" fontWeight={600} color="#38A169">
                                        -${selectedInvoice.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0).toLocaleString() || '0'}
                                    </Typography>
                                </Box>
                                <Divider sx={{ borderStyle: 'dashed' }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography fontWeight={800} color="#C53030">BALANCE DUE</Typography>
                                    <Typography fontWeight={800} color="#C53030">
                                        ${(Number(selectedInvoice.totalAmount) - (selectedInvoice.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0)).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #edf2f7' }}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#263B7C', color: '#263B7C' }}
                                    onClick={() => navigate(`/dashboard/invoices/${selectedInvoice.id}`)}
                                >
                                    View Full Invoice & Print
                                </Button>
                            </Box>
                        </Paper>
                    ) : (
                        <Box sx={{ p: 5, textAlign: 'center', opacity: 0.5 }}>
                            <DescriptionIcon sx={{ fontSize: 64, mb: 2 }} />
                            <Typography>Select an invoice to view details</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>Generate New Invoice</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 4 }}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid size={isSmallMobile ? 12 : 6}>
                            <TextField
                                fullWidth select label="Select Student"
                                value={formData.studentId}
                                onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                            >
                                {students.map(s => <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.grade})</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={isSmallMobile ? 12 : 6}>
                            <TextField
                                fullWidth type="date" label="Due Date"
                                value={formData.dueDate}
                                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid>
                        <Grid size={isSmallMobile ? 12 : 6}>
                            <TextField
                                fullWidth select label="Academic Term"
                                value={formData.term}
                                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                            >
                                <MenuItem value="Term 1">Term 1</MenuItem>
                                <MenuItem value="Term 2">Term 2</MenuItem>
                                <MenuItem value="Term 3">Term 3</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={700}>Invoice Items</Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleLoadFees}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Load Standard Fees
                                </Button>
                            </Stack>
                            {formData.items.map((item, index) => (
                                <Stack key={index} direction={isSmallMobile ? "column" : "row"} spacing={2} sx={{ mt: 1, alignItems: isSmallMobile ? 'stretch' : 'flex-start' }}>
                                    <TextField
                                        fullWidth label="Description"
                                        value={item.description}
                                        onChange={(e) => {
                                            const newItems = [...formData.items];
                                            newItems[index].description = e.target.value;
                                            setFormData({ ...formData, items: newItems });
                                        }}
                                    />
                                    <TextField
                                        sx={{ width: isSmallMobile ? '100%' : 250 }} select label="Income Account"
                                        value={item.incomeAccountId}
                                        onChange={(e) => {
                                            const newItems = [...formData.items];
                                            newItems[index].incomeAccountId = e.target.value;
                                            setFormData({ ...formData, items: newItems });
                                        }}
                                    >
                                        {accounts.filter(a => a.type === 'INCOME').map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                                    </TextField>
                                    <Stack direction="row" spacing={1} sx={{ width: isSmallMobile ? '100%' : 'auto' }}>
                                        <TextField
                                            sx={{ flexGrow: 1, width: isSmallMobile ? 'auto' : 150 }} label="Amount" type="number"
                                            value={item.amount}
                                            onChange={(e) => {
                                                const newItems = [...formData.items];
                                                newItems[index].amount = e.target.value;
                                                setFormData({ ...formData, items: newItems });
                                            }}
                                        />
                                        {formData.items.length > 1 && (
                                            <IconButton onClick={() => handleDeleteItem(index)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={handleAddItem} sx={{ mt: 1 }}>Add Item</Button>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateInvoice} sx={{ bgcolor: '#F9B43F' }}>Generate Invoice</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default InvoicesPage;
